import { Cookies } from "react-cookie";

import { getPlatformUrl } from "../../lib/api";
import { readSseStream } from "./sse-buffer";
import { AssistantSseEvent, parseAssistantSsePayload } from "./type/sse-event";
import { parseRagStreamPayload, RagStreamEvent } from "./type/rag-stream-event";
import { SendMessageReq, ResumeHitlReq } from "./type/chat";

const cookies = new Cookies();

export class RagStreamNotSupportedError extends Error {
  constructor(status: number) {
    super(`RAG stream not supported (${status})`);
    this.name = "RagStreamNotSupportedError";
  }
}

export type AssistantSseHandlers = {
  onEvent: (event: AssistantSseEvent) => void;
  onError?: (error: unknown) => void;
};

export type RagStreamHandlers = {
  onEvent: (event: RagStreamEvent) => void;
  onError?: (error: unknown) => void;
};

function authHeaders(accept: string): Record<string, string> {
  const token = cookies.get("token");
  if (!token) {
    throw new Error("Unauthorized: Token is missing");
  }
  return {
    Authorization: `Bearer ${token}`,
    Accept: accept,
  };
}

function buildRagFormData(req: SendMessageReq): FormData {
  const formData = new FormData();
  formData.append("chat_id", req.chat_id);
  formData.append("chat_no", String(req.chat_no));
  formData.append("query", req.query);
  if (req.model_id) formData.append("model_id", req.model_id);
  if (req.tool_execution_role) formData.append("tool_execution_role", req.tool_execution_role);
  if (req.files) req.files.forEach((file) => formData.append("files", file));
  return formData;
}

/** SSE line parser for ``GET /xcausal/rag/events`` (fetch streaming, not EventSource). */
export async function streamAssistantJobEvents(
  chatIds: string[],
  handlers: AssistantSseHandlers,
  signal: AbortSignal,
): Promise<void> {
  if (chatIds.length === 0) {
    return;
  }
  const params = new URLSearchParams({ chat_ids: chatIds.join(",") });
  const url = `${getPlatformUrl()}/xcausal/rag/events?${params.toString()}`;
  const response = await fetch(url, {
    method: "GET",
    headers: authHeaders("text/event-stream"),
    signal,
  });
  if (!response.ok) {
    throw new Error(`SSE connect failed: ${response.status}`);
  }
  if (!response.body) {
    throw new Error("SSE response has no body");
  }

  try {
    await readSseStream(
      response.body,
      parseAssistantSsePayload,
      handlers.onEvent,
      signal,
    );
  } catch (error) {
    if (signal.aborted) {
      return;
    }
    handlers.onError?.(error);
    throw error;
  }
}

/** ``POST /xcausal/rag/stream`` — LangGraph turn progress (501 → caller falls back to JSON). */
export async function streamRagTurn(
  req: SendMessageReq,
  handlers: RagStreamHandlers,
  signal: AbortSignal,
): Promise<void> {
  const url = `${getPlatformUrl()}/xcausal/rag/stream`;
  const response = await fetch(url, {
    method: "POST",
    headers: authHeaders("text/event-stream"),
    body: buildRagFormData(req),
    signal,
  });
  if (response.status === 501) {
    throw new RagStreamNotSupportedError(response.status);
  }
  if (!response.ok) {
    throw new Error(`RAG stream failed: ${response.status}`);
  }
  if (!response.body) {
    throw new Error("RAG stream response has no body");
  }
  try {
    await readSseStream(response.body, parseRagStreamPayload, handlers.onEvent, signal);
  } catch (error) {
    if (signal.aborted) {
      return;
    }
    handlers.onError?.(error);
    throw error;
  }
}

/** ``POST /xcausal/rag/resume/stream`` — HITL resume with turn progress. */
export async function streamResumeHitl(
  req: ResumeHitlReq,
  handlers: RagStreamHandlers,
  signal: AbortSignal,
): Promise<void> {
  const url = `${getPlatformUrl()}/xcausal/rag/resume/stream`;
  const body: Record<string, unknown> = {
    chat_id: req.chat_id,
    model_id: req.model_id,
    action: req.action,
  };
  if (req.feedback) {
    body.feedback = req.feedback;
  }
  if (req.run_id) {
    body.run_id = req.run_id;
  }
  const response = await fetch(url, {
    method: "POST",
    headers: {
      ...authHeaders("text/event-stream"),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    signal,
  });
  if (response.status === 501) {
    throw new RagStreamNotSupportedError(response.status);
  }
  if (!response.ok) {
    throw new Error(`RAG resume stream failed: ${response.status}`);
  }
  if (!response.body) {
    throw new Error("RAG resume stream response has no body");
  }
  try {
    await readSseStream(response.body, parseRagStreamPayload, handlers.onEvent, signal);
  } catch (error) {
    if (signal.aborted) {
      return;
    }
    handlers.onError?.(error);
    throw error;
  }
}
